import React, { useState, useEffect } from 'react';
import SlidingPane from 'react-sliding-pane';
import 'react-sliding-pane/dist/react-sliding-pane.css';
import { collection, query, getDocs, doc, updateDoc } from 'firebase/firestore';
import { useSearchParams } from 'react-router-dom';
import { db } from '../../../firebase';
import './wapParks.css';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const formatNumber = (num) => num.toLocaleString('en-US', { maximumFractionDigits: 0 });
const formatSignedNumber = (num) => {
  if (typeof num !== 'number' || Number.isNaN(num)) return '--';
  if (num > 0) return `+${formatNumber(num)}`;
  return formatNumber(num);
};
const toTwoDigits = (value) => String(value).padStart(2, '0');
const chartTextColor = '#f3e9de';
const chartGridColor = 'rgba(181, 168, 157, 0.2)';
const whatsappMembersColor = '#25d366';
const PARK_QUERY_PARAM = 'park';
const RECENT_PARK_CHANGE_DAYS = 7;
const ignoredLowestGroups = new Set([
  'pb sightings & chatz',
  'pb guides and rangers',
  'guides and rangers',
  '13-kruger sightings',
]);
const ULTRAMSG_INSTANCE_ID = process.env.REACT_APP_ULTRAMSG_INSTANCE_ID || 'instance113733';
const ULTRAMSG_TOKEN = process.env.REACT_APP_ULTRAMSG_TOKEN || 'ahxwl63bn71dfat2';
const ULTRAMSG_BASE_URL = `https://api.ultramsg.com/${ULTRAMSG_INSTANCE_ID}`;

const buildTargetMonths = (today = new Date()) => {
  const currentYear = today.getFullYear();
  const currentMonthNumber = today.getMonth() + 1;
  const lastMonthDate = new Date(currentYear, currentMonthNumber - 2, 1);
  const targetMonths = [];

  for (let month = 1; month <= currentMonthNumber; month += 1) {
    targetMonths.push({
      yearId: String(currentYear),
      monthId: toTwoDigits(month),
    });
  }

  if (lastMonthDate.getFullYear() !== currentYear) {
    targetMonths.push({
      yearId: String(lastMonthDate.getFullYear()),
      monthId: toTwoDigits(lastMonthDate.getMonth() + 1),
    });
  }

  return targetMonths;
};

const delay = (ms) => new Promise((res) => setTimeout(res, ms));

const extractMemberCount = (data) => {
  if (!data || typeof data !== 'object') return 0;

  const participants =
    data.groupMetadata?.participants ||
    data.participants ||
    data.group?.participants;

  if (Array.isArray(participants)) {
    return participants.length;
  }

  if (typeof data.participantsCount === 'number') {
    return data.participantsCount;
  }

  if (typeof data.size === 'number') {
    return data.size;
  }

  return 0;
};

const shouldRetryRequest = (status, data, rawText = '') => {
  if (status === 429 || status >= 500) return true;

  const message = [
    typeof data === 'object' ? data?.error : '',
    typeof data === 'object' ? data?.message : '',
    typeof data === 'object' ? data?.description : '',
    rawText,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return message.includes('rate') || message.includes('limit') || message.includes('too many');
};

const getLowestEligibleGroup = (groupInfo = []) => {
  for (let index = groupInfo.length - 1; index >= 0; index -= 1) {
    const group = groupInfo[index];
    const normalizedName = String(group?.name || '').trim().toLowerCase();
    if (!normalizedName || ignoredLowestGroups.has(normalizedName)) {
      continue;
    }
    return group;
  }

  return null;
};

const getDeltaToneClassName = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 'wap__delta wap__delta--empty';
  }

  if (value > 0) {
    return 'wap__delta wap__delta--positive';
  }

  if (value < 0) {
    return 'wap__delta wap__delta--negative';
  }

  return 'wap__delta wap__delta--neutral';
};

const getParkSeriesColor = (index, alpha = 1) => {
  const hue = (index * 53 + 142) % 360;
  return `hsla(${hue}, 72%, 58%, ${alpha})`;
};

const WapParks = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [parks, setParks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPark, setCurrentPark] = useState(null);
  const [newApiId, setNewApiId] = useState({ key: '', value: '' });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');
  const [whatsappSummary, setWhatsappSummary] = useState({
    snapshotDate: '',
    totalMembers: 0,
    totalGroups: 0,
    parkCount: 0,
  });
  const [whatsappTrendLabels, setWhatsappTrendLabels] = useState([]);
  const [whatsappTrendData, setWhatsappTrendData] = useState({
    totalMembers: [],
    totalGroups: [],
  });
  const [analyticsParkData, setAnalyticsParkData] = useState([]);
  const [parkTrendData, setParkTrendData] = useState([]);
  const [parkDailyChangeDates, setParkDailyChangeDates] = useState([]);
  const [parkDailyChangeData, setParkDailyChangeData] = useState([]);
  const selectedParkId = searchParams.get(PARK_QUERY_PARAM);

  // Live park fetch should only run on initial page load.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchParks = async () => {
      setLoading(true);
      const getGroupMemberCount = async (groupId, attempt = 1) => {
        const MAX_ATTEMPTS = 6;
        const normalizedId = groupId?.trim();
        if (!normalizedId) return 0;

        const groupCode = normalizedId.endsWith('@g.us') ? normalizedId : `${normalizedId}@g.us`;

        try {
          const params = new URLSearchParams({
            token: ULTRAMSG_TOKEN,
            groupId: groupCode,
          });
          const res = await fetch(`${ULTRAMSG_BASE_URL}/groups/group?${params.toString()}`);

          const text = await res.text();

          let data;
          try {
            data = JSON.parse(text);
          } catch (err) {
            data = null;
          }

          if (!res.ok) {
            if (attempt < MAX_ATTEMPTS) {
              const shouldRetry = shouldRetryRequest(res.status, data, text);
              if (shouldRetry) {
                await delay(attempt * 1000);
                return getGroupMemberCount(groupId, attempt + 1);
              }
            }

            console.warn(`UltraMSG group lookup failed for ${groupCode}`, {
              status: res.status,
              body: data || text,
            });
            return 0;
          }

          return extractMemberCount(data);
        } catch (e) {
          if (attempt < MAX_ATTEMPTS) {
            await delay(attempt * 1000);
            return getGroupMemberCount(groupId, attempt + 1);
          }
          console.error('Failed to fetch UltraMSG group info', e);
          return 0;
        }
      };

      const fetchGroupInfoWithThrottle = async (entries) => {
        const results = [];
        for (const [name, id] of entries) {
          const count = await getGroupMemberCount(id);
          results.push({ name, id, count });
          await delay(1000);
        }
        return results;
      };

      const q = query(collection(db, "whatsAppGroups"));
      const querySnapshot = await getDocs(q);
      const parksData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setParks(parksData);
      setLoading(false);

      for (const park of parksData) {
        if (!park.apiIDs) continue;
        const entries = Object.entries(park.apiIDs);
        const groupInfo = await fetchGroupInfoWithThrottle(entries);

        const totalMembers = groupInfo.reduce((t, g) => t + g.count, 0);
        groupInfo.sort((a, b) => b.count - a.count);
        setParks((prev) =>
          prev.map((p) =>
            p.id === park.id ? { ...p, totalMembers, groupInfo } : p
          )
        );
      }
    };

    fetchParks();
  }, []);

  useEffect(() => {
    const fetchWhatsAppAnalytics = async () => {
      setAnalyticsLoading(true);
      setAnalyticsError('');

      try {
        const targetMonths = buildTargetMonths(new Date());
        const dailySnapshots = [];
        let latestSnapshot = null;

        for (const { yearId, monthId } of targetMonths) {
          const daysCollection = collection(
            db,
            'analytics',
            'whatsappGroups',
            'years',
            yearId,
            'months',
            monthId,
            'days'
          );
          const daysSnapshot = await getDocs(query(daysCollection));

          for (const dayDoc of daysSnapshot.docs) {
            const dayId = toTwoDigits(dayDoc.id);
            const date = new Date(Number(yearId), Number(monthId) - 1, Number(dayId));
            if (Number.isNaN(date.getTime())) {
              continue;
            }

            const snapshotEntry = {
              date: `${yearId}-${monthId}-${dayId}`,
              ref: dayDoc.ref,
              ...dayDoc.data(),
            };
            dailySnapshots.push(snapshotEntry);

            if (!latestSnapshot || snapshotEntry.date.localeCompare(latestSnapshot.date) > 0) {
              latestSnapshot = snapshotEntry;
            }
          }
        }

        const recentSnapshots = [...dailySnapshots]
          .sort((snapshotA, snapshotB) => snapshotA.date.localeCompare(snapshotB.date))
          .slice(-30);
        const parkTrendMap = {};

        setWhatsappTrendLabels(recentSnapshots.map((entry) => entry.date));
        setWhatsappTrendData({
          totalMembers: recentSnapshots.map((entry) => entry.totalMembers || 0),
          totalGroups: recentSnapshots.map((entry) => entry.totalGroups || 0),
        });

        for (const [index, snapshot] of recentSnapshots.entries()) {
          const parksCollection = collection(snapshot.ref, 'parks');
          const parksSnapshot = await getDocs(query(parksCollection));
          const snapshotRunId = snapshot.snapshotRunId || '';

          parksSnapshot.docs
            .map((parkDoc) => ({
              id: parkDoc.id,
              ...parkDoc.data(),
            }))
            .filter((park) => !snapshotRunId || park.snapshotRunId === snapshotRunId)
            .forEach((park) => {
              if (!parkTrendMap[park.id]) {
                parkTrendMap[park.id] = {
                  id: park.id,
                  label: park.parkName || park.id,
                  data: Array(recentSnapshots.length).fill(null),
                  latestTotalMembers: 0,
                };
              }

              parkTrendMap[park.id].data[index] = park.totalMembers || 0;
              parkTrendMap[park.id].latestTotalMembers = park.totalMembers || 0;
            });
        }

        const sortedParkTrendData = Object.values(parkTrendMap)
          .map((park) => {
            const dailyChanges = park.data.map((currentMembers, index, values) => {
              if (index === 0) {
                return null;
              }

              const previousMembers = values[index - 1];

              if (
                typeof currentMembers !== 'number' ||
                Number.isNaN(currentMembers) ||
                typeof previousMembers !== 'number' ||
                Number.isNaN(previousMembers)
              ) {
                return null;
              }

              return currentMembers - previousMembers;
            });

            return {
              ...park,
              dailyChanges,
              latestDailyChange: dailyChanges[dailyChanges.length - 1] ?? null,
            };
          })
          .sort((parkA, parkB) => (parkB.latestTotalMembers || 0) - (parkA.latestTotalMembers || 0));

        const comparisonStartIndex = Math.max(1, recentSnapshots.length - RECENT_PARK_CHANGE_DAYS);
        const comparisonDates = recentSnapshots
          .slice(comparisonStartIndex)
          .map((entry) => entry.date);
        const latestDailyChangeByParkId = sortedParkTrendData.reduce((accumulator, park) => {
          accumulator[park.id] = park.latestDailyChange ?? null;
          return accumulator;
        }, {});

        setParkTrendData(sortedParkTrendData);
        setParkDailyChangeDates(comparisonDates);
        setParkDailyChangeData(
          sortedParkTrendData.map((park) => ({
            id: park.id,
            label: park.label,
            latestTotalMembers: park.latestTotalMembers || 0,
            recentDailyChanges: park.dailyChanges.slice(comparisonStartIndex),
          }))
        );

        if (!latestSnapshot) {
          setWhatsappSummary({
            snapshotDate: '',
            totalMembers: 0,
            totalGroups: 0,
            parkCount: 0,
          });
          setAnalyticsParkData([]);
          setParkTrendData([]);
          setParkDailyChangeDates([]);
          setParkDailyChangeData([]);
          return;
        }

        setWhatsappSummary({
          snapshotDate: latestSnapshot.date,
          totalMembers: latestSnapshot.totalMembers || 0,
          totalGroups: latestSnapshot.totalGroups || 0,
          parkCount: latestSnapshot.parkCount || 0,
        });

        const parksCollection = collection(latestSnapshot.ref, 'parks');
        const parksSnapshot = await getDocs(query(parksCollection));
        const latestSnapshotRunId = latestSnapshot.snapshotRunId || '';

        const parksData = parksSnapshot.docs
          .map((parkDoc) => ({
            id: parkDoc.id,
            ...parkDoc.data(),
          }))
          .filter((park) => !latestSnapshotRunId || park.snapshotRunId === latestSnapshotRunId)
          .map((park) => ({
            ...park,
            latestDailyChange: latestDailyChangeByParkId[park.id] ?? null,
          }))
          .sort((parkA, parkB) => {
            if ((parkB.totalMembers || 0) !== (parkA.totalMembers || 0)) {
              return (parkB.totalMembers || 0) - (parkA.totalMembers || 0);
            }

            return String(parkA.parkName || parkA.id).localeCompare(String(parkB.parkName || parkB.id));
          });

        setAnalyticsParkData(parksData);
      } catch (error) {
        console.error('Error loading WhatsApp analytics:', error);
        setAnalyticsError('Could not load stored WhatsApp analytics.');
        setParkDailyChangeDates([]);
        setParkDailyChangeData([]);
      } finally {
        setAnalyticsLoading(false);
      }
    };

    fetchWhatsAppAnalytics();
  }, []);


  const updateSearchParams = (parkId, replace = false) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    if (parkId) {
      nextSearchParams.set(PARK_QUERY_PARAM, parkId);
    } else {
      nextSearchParams.delete(PARK_QUERY_PARAM);
    }

    setSearchParams(nextSearchParams, { replace });
  };

  const closeSlider = (replace = true) => {
    updateSearchParams(null, replace);
  };

  useEffect(() => {
    if (!selectedParkId) {
      setCurrentPark(null);
      setNewApiId({ key: '', value: '' });
      return;
    }

    const selectedPark = parks.find((park) => park.id === selectedParkId);

    if (selectedPark) {
      if (currentPark?.id !== selectedParkId) {
        setCurrentPark(selectedPark);
        setNewApiId({ key: '', value: '' });
      }
      return;
    }

    if (!loading) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete(PARK_QUERY_PARAM);
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [selectedParkId, parks, loading, currentPark?.id, searchParams, setSearchParams]);

  const openSlider = (parkId) => {
    updateSearchParams(parkId);
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    if (index !== undefined) {
      const updatedGateTimes = currentPark.gateTimes;
      updatedGateTimes[index][name] = value;
      setCurrentPark({ ...currentPark, gateTimes: updatedGateTimes });
    } else {
      setCurrentPark({ ...currentPark, [name]: value });
    }
  };

  const handleAddApiId = () => {
    setCurrentPark({
      ...currentPark,
      apiIDs: {
        ...currentPark.apiIDs,
        [newApiId.key]: newApiId.value
      }
    });
    setNewApiId({ key: '', value: '' });
  };

  const handleSave = async () => {
    const parkRef = doc(db, "whatsAppGroups", currentPark.id);
    await updateDoc(parkRef, currentPark);
    const updatedParks = parks.map(park =>
      park.id === currentPark.id ? currentPark : park
    );
    setParks(updatedParks);
    closeSlider();
  };

  const topAnalyticsParks = analyticsParkData.slice(0, 12);

  return (
    <div className='wap__container'>
      <h1 className='wap__heading'>WhatsApp Parks</h1>

      <section className='wap__analytics-shell'>
        <div className='wap__section-header'>
          <div>
            <h2 className='wap__section-title'>Stored Analytics</h2>
            <p className='wap__section-subtitle'>
              Daily saved snapshots per park. This is the historical view, not the live fetch.
            </p>
          </div>
        </div>

        {analyticsError ? (
          <p className='wap__message wap__message--error'>{analyticsError}</p>
        ) : analyticsLoading ? (
          <p className='wap__message'>Loading stored WhatsApp analytics...</p>
        ) : (
          <>
            <div className='wap__stat-grid'>
              <div className='wap__stat-card'>
                <div className='wap__stat-label'>Latest Snapshot</div>
                <div className='wap__stat-value'>
                  {whatsappSummary.snapshotDate || 'No snapshot yet'}
                </div>
              </div>
              <div className='wap__stat-card'>
                <div className='wap__stat-label'>Total Members</div>
                <div className='wap__stat-value'>{formatNumber(whatsappSummary.totalMembers)}</div>
              </div>
              <div className='wap__stat-card'>
                <div className='wap__stat-label'>Total Groups</div>
                <div className='wap__stat-value'>{formatNumber(whatsappSummary.totalGroups)}</div>
              </div>
              <div className='wap__stat-card'>
                <div className='wap__stat-label'>Parks</div>
                <div className='wap__stat-value'>{formatNumber(whatsappSummary.parkCount)}</div>
              </div>
            </div>

            <div className='wap__chart-stack'>
              <section className='wap__card'>
                <h3 className='wap__card-title'>Members over time</h3>
                <div className='wap__chart'>
                  <Line
                    data={{
                      labels: whatsappTrendLabels,
                      datasets: [
                        {
                          label: 'Members',
                          data: whatsappTrendData.totalMembers,
                          borderColor: whatsappMembersColor,
                          pointBackgroundColor: whatsappMembersColor,
                          borderWidth: 2,
                          fill: false,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { position: 'top', labels: { color: chartTextColor } },
                        tooltip: {
                          callbacks: {
                            label: (context) =>
                              `${context.dataset.label}: ${formatNumber(context.raw)}`,
                          },
                        },
                      },
                      scales: {
                        x: { ticks: { color: chartTextColor }, grid: { color: chartGridColor } },
                        y: { ticks: { color: chartTextColor }, grid: { color: chartGridColor } },
                      },
                    }}
                  />
                </div>
              </section>

              <section className='wap__card'>
                <h3 className='wap__card-title'>Daily Members By Park</h3>
                <div className='wap__chart'>
                  <Line
                    data={{
                      labels: whatsappTrendLabels,
                      datasets: parkTrendData.map((park, index) => {
                        const color = getParkSeriesColor(index);
                        return {
                          label: park.label,
                          data: park.data,
                          borderColor: color,
                          pointBackgroundColor: color,
                          pointBorderColor: color,
                          borderWidth: 2,
                          pointRadius: 3,
                          fill: false,
                          spanGaps: true,
                        };
                      }),
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { position: 'top', labels: { color: chartTextColor } },
                        tooltip: {
                          callbacks: {
                            label: (context) => `${context.dataset.label}: ${formatNumber(context.raw || 0)}`,
                          },
                        },
                      },
                      scales: {
                        x: { ticks: { color: chartTextColor }, grid: { color: chartGridColor } },
                        y: { ticks: { color: chartTextColor }, grid: { color: chartGridColor } },
                      },
                    }}
                  />
                </div>
              </section>

              <section className='wap__card'>
                <h3 className='wap__card-title'>
                  Latest Park Totals{whatsappSummary.snapshotDate ? ` (${whatsappSummary.snapshotDate})` : ''}
                </h3>
                <div className='wap__chart wap__chart--bar'>
                  <Bar
                    data={{
                      labels: topAnalyticsParks.map((park) => park.parkName || park.id),
                      datasets: [
                        {
                          label: 'Members',
                          data: topAnalyticsParks.map((park) => park.totalMembers || 0),
                          backgroundColor: 'rgba(37, 211, 102, 0.7)',
                          borderColor: whatsappMembersColor,
                          borderWidth: 1,
                        },
                      ],
                    }}
                    options={{
                      indexAxis: 'y',
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false, labels: { color: chartTextColor } },
                        tooltip: {
                          callbacks: {
                            label: (context) => `Members: ${formatNumber(context.raw)}`,
                          },
                        },
                      },
                      scales: {
                        x: { ticks: { color: chartTextColor }, grid: { color: chartGridColor } },
                        y: { ticks: { color: chartTextColor }, grid: { color: chartGridColor } },
                      },
                    }}
                  />
                </div>
              </section>
            </div>

            <section className='wap__card'>
              <h3 className='wap__card-title'>Daily Gain / Loss By Park</h3>
              <p className='wap__card-subtitle'>
                Bars above zero mean that park gained members that day. Bars below zero mean it lost members.
              </p>
              {parkDailyChangeData.length && parkDailyChangeDates.length ? (
                <div className='wap__chart'>
                  <Bar
                    data={{
                      labels: parkDailyChangeDates,
                      datasets: parkDailyChangeData.map((park, index) => ({
                        label: park.label,
                        data: park.recentDailyChanges,
                        backgroundColor: getParkSeriesColor(index, 0.5),
                        borderColor: getParkSeriesColor(index),
                        borderWidth: 1,
                        borderRadius: 4,
                      })),
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      interaction: {
                        mode: 'index',
                        intersect: false,
                      },
                      plugins: {
                        legend: { position: 'top', labels: { color: chartTextColor } },
                        tooltip: {
                          callbacks: {
                            label: (context) =>
                              `${context.dataset.label}: ${formatSignedNumber(context.raw)}`,
                          },
                        },
                      },
                      scales: {
                        x: { ticks: { color: chartTextColor }, grid: { color: chartGridColor } },
                        y: {
                          ticks: { color: chartTextColor },
                          grid: { color: chartGridColor },
                        },
                      },
                    }}
                  />
                </div>
              ) : (
                <p className='wap__message'>
                  Not enough stored daily snapshots yet to compare gain or loss by park.
                </p>
              )}
            </section>

            <section className='wap__card'>
              <h3 className='wap__card-title'>
                Latest Stored Park Breakdown
                {whatsappSummary.snapshotDate ? ` (${whatsappSummary.snapshotDate})` : ''}
              </h3>
              <div className='wap__table-wrap'>
                <table className='wap__table'>
                  <thead>
                    <tr>
                      <th>Park</th>
                      <th>Total Members</th>
                      <th>Gain / Loss vs Prev Day</th>
                      <th>Groups</th>
                      <th>Successful Lookups</th>
                      <th>Failed Lookups</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsParkData.length ? (
                      analyticsParkData.map((park) => (
                        <tr key={park.id}>
                          <td>{park.parkName || park.id}</td>
                          <td>{formatNumber(park.totalMembers || 0)}</td>
                          <td>
                            <span className={getDeltaToneClassName(park.latestDailyChange)}>
                              {formatSignedNumber(park.latestDailyChange)}
                            </span>
                          </td>
                          <td>{formatNumber(park.groupCount || 0)}</td>
                          <td>{formatNumber(park.successfulLookups || 0)}</td>
                          <td>{formatNumber(park.failedLookups || 0)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td className='wap__table-empty' colSpan={6}>
                          No stored WhatsApp analytics captured yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </section>

      <section className='wap__live-shell'>
        <div className='wap__section-header'>
          <div>
            <h2 className='wap__section-title'>Live Park Fetch</h2>
            <p className='wap__section-subtitle'>
              Current live totals pulled from UltraMSG when this page opens.
            </p>
          </div>
        </div>

        {loading ? (
          <p className='wap__message'>Loading live WhatsApp park totals...</p>
        ) : (
          <ul className='wap__list'>
            {parks.map(park => {
              const lowestGroup = getLowestEligibleGroup(park.groupInfo);

              return (
              <li key={park.id} className='wap__list-item'>
                <div className='wap__list-copy'>
                  <span>
                    {park.parkName}
                    {park.totalMembers !== undefined
                      ? ` (${park.totalMembers.toLocaleString()})`
                      : ' (fetching)'}
                  </span>
                </div>
                <div className='wap__list-actions'>
                  {lowestGroup ? (
                    <span className='wap__list-meta wap__list-meta--inline'>
                      Lowest group: {lowestGroup.name} ({formatNumber(lowestGroup.count || 0)})
                    </span>
                  ) : null}
                  <button
                    className='wap__list-item-button'
                    onClick={() => openSlider(park.id)}
                  >
                    View Details
                  </button>
                </div>
              </li>
              );
            })}
          </ul>
        )}
      </section>
      <SlidingPane
        isOpen={Boolean(selectedParkId)}
        title='Park Details'
        className="park-details-pane"
        onRequestClose={() => closeSlider()}
      >
        {currentPark ? (
          <Box
            component="form"
            className="park-details-form"
            sx={{
              '& .MuiTextField-root': { m: 1, width: '25ch' },
            }}
            noValidate
            autoComplete="off"
          >
            <div className="park-details-toolbar">
              <Button
                variant="outlined"
                className="park-details-close-button"
                aria-label="Close park details"
                onClick={() => closeSlider()}
              >
                X
              </Button>
            </div>
            <div className="park-details-section">
              <TextField
                variant="outlined"
                id="parkName"
                label="Park Name"
                name="parkName"
                value={currentPark.parkName}
                onChange={handleInputChange}
              />
              <TextField
                variant="outlined"
                id="linkToJoin"
                label="Link To Join"
                name="linkToJoin"
                value={currentPark.linkToJoin}
                onChange={handleInputChange}
              />
            </div>
            <div className="park-details-section">
              <h3>API IDs</h3>
              {currentPark.apiIDs &&
                Object.entries(currentPark.apiIDs).map(([key, value]) => {
                  const info = currentPark.groupInfo?.find((g) => g.id === value);
                  return (
                    <div key={key} className="apiid-field">
                      <TextField
                        variant="outlined"
                        id={key}
                        label={key}
                        name={key}
                        value={value}
                        onChange={handleInputChange}
                      />
                      {info && (
                        <span className="member-count">{info.count.toLocaleString()} members</span>
                      )}
                    </div>
                  );
                })}
              <div className="new-apiid-inputs">
                <TextField
                  variant="outlined"
                  id="newApiKeyId"
                  label="New Group Name"
                  value={newApiId.key}
                  onChange={(e) => setNewApiId({ ...newApiId, key: e.target.value })}
                />
                <TextField
                  variant="outlined"
                  id="newApiValueId"
                  label="New Group API Code"
                  value={newApiId.value}
                  onChange={(e) => setNewApiId({ ...newApiId, value: e.target.value })}
                />
                <Button variant="contained" color="primary" onClick={handleAddApiId}>
                  Add API ID
                </Button>
              </div>
            </div>
            {currentPark.groupInfo ? (
              <div className="park-details-section">
                <h3>Groups</h3>
                <ul className="group-info-list">
                  {currentPark.groupInfo.map((g) => (
                    <li key={g.id} className="group-info-item">
                      {g.name} - {g.count.toLocaleString()} members
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="park-details-section">Loading group info...</div>
            )}
            {currentPark.gateTimes && currentPark.gateTimes.length > 0 && (
              <div className="park-details-section">
                {currentPark.gateTimes.map((time, index) => (
                  <div key={index} className="gate-time-section">
                    <h3>Gate Time {index + 1}</h3>
                    {time.months && time.months.length > 0 && time.months.map((month, monthIndex) => (
                      <TextField
                        key={monthIndex}
                        variant="outlined"
                        id={`months${index}-${monthIndex}`}
                        label="Month"
                        name="month"
                        value={month}
                        onChange={(e) => handleInputChange(e, index)}
                        disabled
                      />
                    ))}
                    <TextField
                      variant="outlined"
                      id={`campOpen${index}`}
                      label="Camp Open"
                      name="campOpen"
                      value={time.campOpen}
                      onChange={(e) => handleInputChange(e, index)}
                      disabled
                    />
                    <TextField
                      variant="outlined"
                      id={`gatesOpen${index}`}
                      label="Gates Open"
                      name="gatesOpen"
                      value={time.gatesOpen}
                      onChange={(e) => handleInputChange(e, index)}
                      disabled
                    />
                    <TextField
                      variant="outlined"
                      id={`campClose${index}`}
                      label="Camp Close"
                      name="campClose"
                      value={time.campClose}
                      onChange={(e) => handleInputChange(e, index)}
                      disabled
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="park-details-section">
              <Button variant="contained" color="primary" onClick={handleSave}>
                Save
              </Button>
            </div>
          </Box>
        ) : (
          <div className="park-details-empty">Loading park details...</div>
        )}
      </SlidingPane>
    </div>
  );
};

export default WapParks;
